import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CoinPurchasePanel from '../components/CoinPurchasePanel';
import '../components/CoinPurchasePanel.css';
import { COINS_DISABLED } from '../config/coinsFlag';
import { exportCvElementToPdf } from '../utils/localCvPdf';
import { markCvPreviewOpen, clearCvPreviewMark, hasFreshCvPreviewMark } from '../utils/cvPreviewRestore';
import { diag, DIAG_ENABLED } from '../utils/diagLog';
import { buildShareUrl } from '../utils/shareUtils';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { 
  ArrowLeft, ArrowRight, FileText, User, Briefcase, GraduationCap, 
  Star, Globe, Award, Save, Download, Eye,
  Plus, Trash2, ChevronDown, Loader2, Users, CloudOff, Check, X,
  Link, Copy, Lock, Sparkles, Coins, ChevronRight
} from 'lucide-react';
import './CVBuilder.css';

// ============================================
// CV TYPE CONFIGURATION
// ============================================

const CV_TYPES = [
  { key: "professional", labelKey: "cvBuilder.types.professional" },
  { key: "normal", labelKey: "cvBuilder.types.normal" },
];

const CV_SECTIONS = {
  professional: [
    { id: "summary", titleKey: "cvBuilder.sections.summary", icon: FileText },
    { id: "personal", titleKey: "cvBuilder.sections.personal", icon: User },
    { id: "employment", titleKey: "cvBuilder.sections.employment", icon: Briefcase },
    { id: "skills", titleKey: "cvBuilder.sections.skills", icon: Star },
    { id: "certifications", titleKey: "cvBuilder.sections.certifications", icon: Award },
    { id: "education", titleKey: "cvBuilder.sections.education", icon: GraduationCap },
    { id: "languages", titleKey: "cvBuilder.sections.languages", icon: Globe },
    { id: "references", titleKey: "cvBuilder.sections.references", icon: Users },
  ],
  normal: [
    { id: "personal", titleKey: "cvBuilder.sections.personal", icon: User },
    { id: "objective", titleKey: "cvBuilder.sections.objective", icon: FileText },
    { id: "experience", titleKey: "cvBuilder.sections.experience", icon: Briefcase },
    { id: "education", titleKey: "cvBuilder.sections.education", icon: GraduationCap },
    { id: "skills", titleKey: "cvBuilder.sections.skills", icon: Star },
  ],
};

const CV_TYPE_ICONS = {
  professional: Briefcase,
  normal: FileText
};

// ============================================
// SECTION WEIGHTS FOR CV STRENGTH
// ============================================

// ============================================
// CV SCORING CONFIG — single source of truth
// ============================================
// Core sections sum to 100%. References = optional bonus capped at +5%.
const CV_SCORING = {
  core: {
    personal: 20,
    summary: 15,
    employment: 25,
    education: 20,
    skills: 20,
  },
  bonus: {
    references: 5,
  },
  // Legacy aliases for resume types that use different section names
  aliases: {
    objective: 'summary',
    experience: 'employment',
  },
};

// Flatten for backward compat
const SECTION_WEIGHTS = {
  personal: CV_SCORING.core.personal,
  summary: CV_SCORING.core.summary,
  employment: CV_SCORING.core.employment,
  education: CV_SCORING.core.education,
  skills: CV_SCORING.core.skills,
  references: CV_SCORING.bonus.references,
  objective: CV_SCORING.core.summary,
  experience: CV_SCORING.core.employment,
  languages: 0,
  certifications: 0,
};

// ============================================
// DEGREE OPTIONS FOR EDUCATION
// ============================================

const DEGREE_OPTIONS = [
  { value: "diploma", labelEn: "Diploma", labelAr: "دبلوم" },
  { value: "bachelor", labelEn: "Bachelor", labelAr: "بكالوريوس" },
  { value: "master", labelEn: "Master", labelAr: "ماجستير" },
  { value: "doctorate", labelEn: "Doctorate (PhD)", labelAr: "دكتوراه" },
  { value: "professor", labelEn: "Professor", labelAr: "بروفيسور" },
  { value: "other", labelEn: "Other", labelAr: "أخرى" },
];

// iter294 — Helper map used by the CV PREVIEW to translate the value
// keys we store (`bachelor`, `master`, `native`, `advanced`, …) into
// the user's current language. Previously the preview rendered the
// raw English value key inside an Arabic CV.
const DEGREE_LABEL_MAP = DEGREE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = { en: o.labelEn, ar: o.labelAr };
  return acc;
}, {});

const LANGUAGE_LEVEL_LABEL_MAP = {
  native:       { en: "Native",       ar: "اللغة الأم" },
  fluent:       { en: "Fluent",       ar: "طلق" },
  advanced:     { en: "Advanced",     ar: "متقدم" },
  intermediate: { en: "Intermediate", ar: "متوسط" },
  basic:        { en: "Basic",        ar: "أساسي" },
  beginner:     { en: "Beginner",     ar: "مبتدئ" },
};

// Returns the localized label for a stored system value. If the value
// is not a known key (user typed a custom degree / language level),
// the raw value is returned unchanged so we never strip user input.
function localizeSystemValue(value, lang, map) {
  if (!value) return "";
  const key = String(value).trim().toLowerCase();
  const entry = map[key];
  if (!entry) return value;
  return lang === "ar" ? entry.ar : entry.en;
}

// ============================================
// AUTO-SAVE UTILITIES
// ============================================

const getDraftKey = (userId, cvType) => `wassla.cvDraft.v2.${userId}.${cvType}`;

// Empty reference factory
const emptyReference = () => ({
  fullName: '',
  institution: '',
  post: '',
  address: '',
  email: '',
  phone: ''
});

// ============================================
// SEPARATE INITIAL STATE FOR EACH CV TYPE
// ============================================

// Professional Resume: Full featured with summary, certs, references, languages
const getInitialProfessionalState = (lang) => ({
  language: lang,
  cv_type: 'professional',
  personal: {
    full_name: '',
    email: '',
    phone: '',
    phones: [''],
    state_key: '',
    city: '',
    linkedin: '',
    links: [{ label: 'LinkedIn', url: '' }],
    address: ''
  },
  summary: '',
  employment: [],
  education: [],
  skills: [],
  languages: [],
  certifications: [],
  references: []
});

// Normal Resume: Simpler with objective, basic experience
const getInitialNormalState = (lang) => ({
  language: lang,
  cv_type: 'normal',
  personal: {
    full_name: '',
    email: '',
    phone: '',
    phones: [''],
    state_key: '',
    city: ''
  },
  objective: '',
  experience: [],
  education: [],
  skills: []
});

// Get initial state based on type
const getInitialCvState = (lang, cvType = 'professional') => {
  return cvType === 'normal' 
    ? getInitialNormalState(lang) 
    : getInitialProfessionalState(lang);
};

// Check if CV is savable for draft (only full name required)
const isCvSavable = (cv) => {
  const name = (cv?.personal?.full_name || '').trim();
  return name.length > 0;
};

// Email validation helper
const isValidEmail = (email) => {
  if (!email || !email.trim()) return true; // Empty is valid
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Check if an entry has meaningful data (not empty)
const isNonEmptyEmployment = (job) => {
  return Boolean(
    (job?.title || '').trim() ||
    (job?.company || '').trim() ||
    (job?.start || '').trim() ||
    (job?.end || '').trim()
  );
};

const isNonEmptyEducation = (edu) => {
  return Boolean(
    (edu?.institution || '').trim() ||
    (edu?.degree || '').trim() ||
    (edu?.major || '').trim()
  );
};

const isNonEmptyCertification = (cert) => {
  return Boolean(
    (cert?.name || '').trim() ||
    (cert?.issuer || '').trim()
  );
};

const isNonEmptyReference = (ref) => {
  return Boolean(
    (ref?.fullName || '').trim() ||
    (ref?.institution || '').trim() ||
    (ref?.email || '').trim()
  );
};

const isNonEmptyLanguage = (lang) => {
  return Boolean((lang?.name || '').trim());
};

// Count only non-empty entries for badges
const countNonEmpty = (arr, checker) => {
  return (arr || []).filter(checker).length;
};

// ============================================
// VALIDATION: DRAFT (Permissive) vs FINAL (Strict)
// ============================================

// Draft validation - only requires Full Name
const validateDraft = (cv, t) => {
  const errors = [];
  
  const fullName = (cv?.personal?.full_name || '').trim();
  if (!fullName) {
    errors.push({ 
      field: 'personal.full_name', 
      message: t('cvBuilder.validation.fullNameRequired') 
    });
  }
  
  // Email format validation (if provided)
  const email = (cv?.personal?.email || '').trim();
  if (email && !isValidEmail(email)) {
    errors.push({ 
      field: 'personal.email', 
      message: t('cvBuilder.validation.invalidEmail') 
    });
  }
  
  return errors;
};

// Final/Export validation - strict rules for complete entries
const validateFinal = (cv, t) => {
  const errors = [];
  
  // Full name is required
  const fullName = (cv?.personal?.full_name || '').trim();
  if (!fullName) {
    errors.push({ 
      field: 'personal.full_name', 
      message: t('cvBuilder.validation.fullNameRequired') 
    });
  }
  
  // Email format validation
  const email = (cv?.personal?.email || '').trim();
  if (email && !isValidEmail(email)) {
    errors.push({ 
      field: 'personal.email', 
      message: t('cvBuilder.validation.invalidEmail') 
    });
  }
  
  // Employment: only validate entries that have meaningful data
  const employment = Array.isArray(cv?.employment) ? cv.employment : [];
  employment.forEach((job, idx) => {
    if (isNonEmptyEmployment(job)) {
      if (!(job?.title || '').trim()) {
        errors.push({ 
          field: `employment[${idx}].title`, 
          message: t('cvBuilder.validation.jobTitleRequired', { num: idx + 1 }) 
        });
      }
      if (!(job?.company || '').trim()) {
        errors.push({ 
          field: `employment[${idx}].company`, 
          message: t('cvBuilder.validation.companyRequired', { num: idx + 1 }) 
        });
      }
      if (!(job?.start || '').trim()) {
        errors.push({ 
          field: `employment[${idx}].start`, 
          message: t('cvBuilder.validation.startDateRequired', { num: idx + 1 }) 
        });
      }
    }
  });
  
  // Education: only validate entries that have meaningful data
  const education = Array.isArray(cv?.education) ? cv.education : [];
  education.forEach((edu, idx) => {
    if (isNonEmptyEducation(edu)) {
      if (!(edu?.institution || '').trim()) {
        errors.push({ 
          field: `education[${idx}].institution`, 
          message: t('cvBuilder.validation.institutionRequired', { num: idx + 1 }) 
        });
      }
      if (!(edu?.degree || '').trim()) {
        errors.push({ 
          field: `education[${idx}].degree`, 
          message: t('cvBuilder.validation.degreeRequired', { num: idx + 1 }) 
        });
      }
    }
  });
  
  // References: only validate entries that have meaningful data
  const references = Array.isArray(cv?.references) ? cv.references : [];
  references.forEach((ref, idx) => {
    if (isNonEmptyReference(ref)) {
      if (!(ref?.fullName || '').trim()) {
        errors.push({ 
          field: `references[${idx}].fullName`, 
          message: t('cvBuilder.validation.refNameRequired', { num: idx + 1 }) 
        });
      }
    }
  });
  
  return errors;
};

// Scroll to invalid field
const scrollToField = (fieldPath) => {
  const el = document.querySelector(`[data-field="${fieldPath}"]`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus?.();
    el.classList?.add('field-error');
    setTimeout(() => el.classList?.remove('field-error'), 3000);
  }
};

// ============================================
// NORMALIZE CV DATA FOR PREVIEW
// ============================================

// Normalize CV data to prevent undefined/null crashes
const normalizeCv = (raw) => {
  const cv = raw || {};
  const personal = cv.personal || {};
  // Migrate single phone -> phones array
  const phones = Array.isArray(personal.phones) && personal.phones.length > 0
    ? personal.phones
    : personal.phone ? [personal.phone] : [''];
  // Migrate state_key -> city (free-text)
  const city = (personal.city || personal.state_key || '').toString();
  // Migrate linkedin -> links array
  const links = Array.isArray(personal.links) && personal.links.length > 0
    ? personal.links
    : personal.linkedin ? [{ label: 'LinkedIn', url: personal.linkedin }] : [{ label: 'LinkedIn', url: '' }];
  return {
    ...cv,
    cv_type: cv.cv_type || 'professional',
    summary: (cv.summary || '').toString(),
    personal: {
      full_name: (personal.full_name || '').toString(),
      email: (personal.email || '').toString(),
      phone: phones[0] || '',
      phones,
      state_key: (personal.state_key || '').toString(),
      city,
      linkedin: (personal.linkedin || links.find(l => l.label === 'LinkedIn')?.url || '').toString(),
      links,
      address: (personal.address || '').toString(),
    },
    employment: Array.isArray(cv.employment) ? cv.employment : [],
    education: Array.isArray(cv.education) ? cv.education : [],
    certifications: Array.isArray(cv.certifications) ? cv.certifications : [],
    skills: Array.isArray(cv.skills) ? cv.skills : [],
    languages: Array.isArray(cv.languages) ? cv.languages : [],
    references: Array.isArray(cv.references) ? cv.references : [],
  };
};

// Helper: filter out completely empty items for clean preview
const hasAnyValue = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  return Object.values(obj).some((v) => {
    if (Array.isArray(v)) return v.length > 0;
    if (v === null || v === undefined) return false;
    return String(v).trim().length > 0;
  });
};

const cleanList = (list) => {
  return (list || []).filter((x) => hasAnyValue(x));
};

// ============================================
// CITY/STATE SEARCHABLE INPUT
// ============================================

const LINK_LABELS = [
  { value: 'LinkedIn', en: 'LinkedIn', ar: 'لينكد إن' },
  { value: 'Portfolio', en: 'Portfolio', ar: 'معرض الأعمال' },
  { value: 'GitHub', en: 'GitHub', ar: 'GitHub' },
  { value: 'Behance', en: 'Behance', ar: 'Behance' },
  { value: 'Website', en: 'Website', ar: 'موقع إلكتروني' },
  { value: 'Other', en: 'Other', ar: 'آخر' },
];

function CityInput({ value, onChange, states, lang }) {
  const [focused, setFocused] = React.useState(false);
  const inputVal = value || '';
  const suggestions = inputVal.trim().length > 0 ? states.filter(s => {
    const name = lang === 'ar' ? s.name_ar : s.name_en;
    return name.toLowerCase().includes(inputVal.toLowerCase());
  }).slice(0, 6) : [];

  return (
    <div className="city-input-wrapper" data-testid="city-input-wrapper">
      <Input
        value={inputVal}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
        placeholder={lang === 'ar' ? 'اكتب المدينة أو الولاية' : 'Type city or state'}
        data-testid="cv-city"
      />
      {focused && suggestions.length > 0 && (
        <div className="city-suggestions" data-testid="city-suggestions">
          {suggestions.map(s => (
            <button
              key={s.key}
              className="city-suggestion-item"
              onMouseDown={(e) => { e.preventDefault(); onChange(lang === 'ar' ? s.name_ar : s.name_en); setFocused(false); }}
            >
              {lang === 'ar' ? s.name_ar : s.name_en}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// DATE INPUT COMPONENT (Full date with day)
// ============================================

function DateInput({ value, onChange, placeholder, min, max, className = '' }) {
  return (
    <input
      type="date"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      min={min}
      max={max}
      className={`date-input ${className}`}
      placeholder={placeholder}
      data-testid="date-input"
    />
  );
}

// ============================================
// DEGREE SELECT COMPONENT
// ============================================

function DegreeSelect({ value, onChange, lang = 'en' }) {
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="degree-select">
        <SelectValue placeholder={lang === 'ar' ? 'اختر المؤهل' : 'Select degree'} />
      </SelectTrigger>
      <SelectContent>
        {DEGREE_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {lang === 'ar' ? opt.labelAr : opt.labelEn}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ============================================
// REFERENCE FORM COMPONENT
// ============================================

function ReferenceForm({ refItem, onChange, onRemove, index, t }) {
  return (
    <div className="list-item reference-item">
      <div className="item-header">
        <span className="item-number">{index + 1}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="remove-btn"
        >
          <Trash2 size={16} />
        </Button>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <Label>{t('cvBuilder.fields.refFullName')} *</Label>
          <Input
            value={refItem.fullName || ''}
            onChange={(e) => onChange({ ...refItem, fullName: e.target.value })}
            placeholder={t('cvBuilder.fields.refFullName')}
          />
        </div>
        <div className="form-group">
          <Label>{t('cvBuilder.fields.refInstitution')}</Label>
          <Input
            value={refItem.institution || ''}
            onChange={(e) => onChange({ ...refItem, institution: e.target.value })}
            placeholder={t('cvBuilder.fields.refInstitution')}
          />
        </div>
        <div className="form-group">
          <Label>{t('cvBuilder.fields.refPost')}</Label>
          <Input
            value={refItem.post || ''}
            onChange={(e) => onChange({ ...refItem, post: e.target.value })}
            placeholder={t('cvBuilder.fields.refPost')}
          />
        </div>
        <div className="form-group">
          <Label>{t('cvBuilder.fields.refAddress')}</Label>
          <Input
            value={refItem.address || ''}
            onChange={(e) => onChange({ ...refItem, address: e.target.value })}
            placeholder={t('cvBuilder.fields.refAddress')}
          />
        </div>
        <div className="form-group">
          <Label>{t('cvBuilder.fields.refEmail')}</Label>
          <Input
            type="email"
            value={refItem.email || ''}
            onChange={(e) => onChange({ ...refItem, email: e.target.value })}
            placeholder={t('cvBuilder.fields.refEmail')}
          />
        </div>
        <div className="form-group">
          <Label>{t('cvBuilder.fields.refPhone')}</Label>
          <Input
            type="tel"
            value={refItem.phone || ''}
            onChange={(e) => onChange({ ...refItem, phone: e.target.value })}
            placeholder={t('cvBuilder.fields.refPhone')}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN CV BUILDER COMPONENT
// ============================================

function CVBuilder() {
  const { t, i18n } = useTranslation();
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track if user arrived from the CV landing page with a plan pre-selected
  const arrivedWithPlan = useRef(location.state?.plan || null);
  const lang = i18n.language || 'ar';
  const isRTL = lang === 'ar';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [unlockingPro, setUnlockingPro] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [states, setStates] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showCoinInfo, setShowCoinInfo] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');
  
  // Dirty tracking for save button
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const initialLoadRef = useRef(true);
  
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [localExporting, setLocalExporting] = useState(false);

  // Android: if the WebView renderer was killed while the CV preview was open,
  // the activity is recreated and the app cold-boots — reopen the preview once
  // the draft has loaded so the user lands exactly where they left off.
  const pendingPreviewRestore = useRef(hasFreshCvPreviewMark());
  useEffect(() => {
    if (draftLoaded && pendingPreviewRestore.current) {
      pendingPreviewRestore.current = false;
      diag('PREVIEW_RESTORED_AFTER_RECREATE');
      setShowPreviewModal(true);
      setTimeout(() => {
        try {
          const saved = Number(localStorage.getItem('wassla_cv_preview_scroll'));
          const scroller = document.querySelector('.cv-preview-overlay');
          if (saved && scroller) scroller.scrollTop = saved;
        } catch (_e) { /* noop */ }
      }, 600);
    }
  }, [draftLoaded]);
  useEffect(() => {
    diag('PREVIEW_MODAL', { open: showPreviewModal });
    if (showPreviewModal) markCvPreviewOpen();
    else if (!pendingPreviewRestore.current) clearCvPreviewMark();
  }, [showPreviewModal]);
  const [showProPreview, setShowProPreview] = useState(null); // holds template id for Canva-style preview

  // iter294 — Live wallet balance. We fetch from /api/wallet (the
  // single source of truth) and reuse it on the Unlock Pro button +
  // anywhere else the CV Builder needs to show the coin balance. This
  // replaces the stale `user.coins` read from auth context.
  const [liveBalance, setLiveBalance] = useState(null);
  React.useEffect(() => {
    if (!token) return undefined;
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wallet`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancel && typeof data.balance === 'number') {
          setLiveBalance(data.balance);
          // Also sync the auth context so the header pill matches.
          if (updateUser && data.balance !== user?.coins) {
            try { updateUser({ coins: data.balance }); } catch (_e) { /* best effort */ }
          }
        }
      } catch (_e) { /* best effort — fallback to user.coins */ }
    })();
    return () => { cancel = true; };
  }, [token, updateUser, user]);

  // CV Progress Streak
  const [streak, setStreak] = useState({ days: 0, last_date: null, milestones_claimed: [] });
  
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    summary: true,
    objective: true,
    employment: false,
    experience: false,
    education: false,
    skills: false,
    languages: false,
    certifications: false,
    references: false
  });

  // ============================================
  // SEPARATE STATE FOR EACH CV TYPE
  // ============================================
  
  const [selectedType, setSelectedType] = useState('professional');
  
  // Professional CV data (separate from Normal)
  const [professionalCv, setProfessionalCv] = useState(() => getInitialProfessionalState(lang));
  
  // Normal CV data (separate from Professional)
  const [normalCv, setNormalCv] = useState(() => getInitialNormalState(lang));

  // Deterministic persistence before backgrounding (native lifecycle safety):
  // flush the current draft immediately (bypassing the 500ms debounce) and
  // record the preview scroll position so a recovery reload can restore the
  // user exactly where they were.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState !== 'hidden') return;
      try {
        if (user?.user_id && draftLoaded) {
          const draftKey = getDraftKey(user.user_id, selectedType);
          const dataToSave = selectedType === 'professional' ? professionalCv : normalCv;
          localStorage.setItem(draftKey, JSON.stringify(dataToSave));
        }
        if (showPreviewModal) {
          const scroller = document.querySelector('.cv-preview-overlay');
          if (scroller) localStorage.setItem('wassla_cv_preview_scroll', String(scroller.scrollTop));
        }
      } catch (_e) { /* noop */ }
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [user?.user_id, draftLoaded, selectedType, professionalCv, normalCv, showPreviewModal]);
  
  // Get current CV based on selected type
  const cv = selectedType === 'professional' ? professionalCv : normalCv;
  const setCv = selectedType === 'professional' ? setProfessionalCv : setNormalCv;
  
  const [skillInput, setSkillInput] = useState('');
  const skipNextAutoSave = useRef(false);

  // Autofill state
  const [autofillData, setAutofillData] = useState(null);
  const autofillApplied = useRef(false);

  // Pro access state
  const [hasProAccess, setHasProAccess] = useState(false);
  // Pro price comes from the server catalog (single source of truth).
  const [proPrice, setProPrice] = useState(10);

  // Public CV link state
  const [publicUsername, setPublicUsername] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [settingUsername, setSettingUsername] = useState(false);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const currentSections = CV_SECTIONS[selectedType] || CV_SECTIONS.professional;

  // ============================================
  // DIRTY TRACKING
  // ============================================
  
  // Mark as dirty whenever cv changes (after initial load)
  useEffect(() => {
    if (initialLoadRef.current) {
      return;
    }
    setIsDirty(true);
  }, [professionalCv, normalCv]);

  // ============================================
  // TYPE SWITCHING HANDLER
  // ============================================
  
  const handleTypeChange = useCallback(async (newType) => {
    if (newType === selectedType) return;
    
    console.log(`[CVBuilder] Switching from ${selectedType} to ${newType}`);
    
    // Save current draft before switching
    if (user?.user_id) {
      try {
        const currentDraftKey = getDraftKey(user.user_id, selectedType);
        const currentData = selectedType === 'professional' ? professionalCv : normalCv;
        localStorage.setItem(currentDraftKey, JSON.stringify(currentData));
        console.log(`[CVBuilder] Saved ${selectedType} draft before switching`);
      } catch (e) {
        console.error('[CVBuilder] Error saving draft before switch:', e);
      }
    }
    
    // Load draft for the new type
    if (user?.user_id) {
      try {
        const newDraftKey = getDraftKey(user.user_id, newType);
        const raw = localStorage.getItem(newDraftKey);
        if (raw) {
          const draft = JSON.parse(raw);
          if (newType === 'professional') {
            setProfessionalCv(prev => ({
              ...getInitialProfessionalState(lang),
              ...draft,
              personal: { ...getInitialProfessionalState(lang).personal, ...draft.personal }
            }));
          } else {
            setNormalCv(prev => ({
              ...getInitialNormalState(lang),
              ...draft,
              personal: { ...getInitialNormalState(lang).personal, ...draft.personal }
            }));
          }
          console.log(`[CVBuilder] Loaded ${newType} draft from localStorage`);
        }
      } catch (e) {
        console.error('[CVBuilder] Error loading draft for new type:', e);
      }
    }
    
    setSelectedType(newType);
    setIsDirty(false);
    skipNextAutoSave.current = true;
  }, [selectedType, user?.user_id, professionalCv, normalCv, lang]);

  // ============================================
  // AUTO-SAVE LOGIC (TYPE-SPECIFIC)
  // ============================================
  
  // Load draft on mount for BOTH types
  useEffect(() => {
    if (!user?.user_id) return;
    
    // Load Professional draft
    try {
      const proDraftKey = getDraftKey(user.user_id, 'professional');
      const proRaw = localStorage.getItem(proDraftKey);
      if (proRaw) {
        const proDraft = JSON.parse(proRaw);
        setProfessionalCv(prev => ({
          ...getInitialProfessionalState(lang),
          ...proDraft,
          personal: { ...getInitialProfessionalState(lang).personal, ...proDraft.personal },
          references: Array.isArray(proDraft.references) ? proDraft.references : []
        }));
        console.log('[CVBuilder] Professional draft loaded');
      }
    } catch (e) {
      console.error('[CVBuilder] Error loading professional draft:', e);
    }
    
    // Load Normal draft
    try {
      const normalDraftKey = getDraftKey(user.user_id, 'normal');
      const normalRaw = localStorage.getItem(normalDraftKey);
      if (normalRaw) {
        const normalDraft = JSON.parse(normalRaw);
        setNormalCv(prev => ({
          ...getInitialNormalState(lang),
          ...normalDraft,
          personal: { ...getInitialNormalState(lang).personal, ...normalDraft.personal }
        }));
        console.log('[CVBuilder] Normal draft loaded');
      }
    } catch (e) {
      console.error('[CVBuilder] Error loading normal draft:', e);
    }
    
    setDraftLoaded(true);
    setTimeout(() => {
      initialLoadRef.current = false;
    }, 500);
  }, [user?.user_id, lang]);

  // Auto-save current type's draft
  useEffect(() => {
    if (!user?.user_id || !draftLoaded) return;
    
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }

    setAutoSaveStatus('saving');
    
    const timeoutId = setTimeout(() => {
      try {
        // Save the current type's data
        const draftKey = getDraftKey(user.user_id, selectedType);
        const dataToSave = selectedType === 'professional' ? professionalCv : normalCv;
        localStorage.setItem(draftKey, JSON.stringify(dataToSave));
        setAutoSaveStatus('saved');
        console.log(`[CVBuilder] ${selectedType} draft auto-saved`);
      } catch (e) {
        console.error('[CVBuilder] Error auto-saving draft:', e);
        setAutoSaveStatus('error');
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [professionalCv, normalCv, selectedType, user?.user_id, draftLoaded]);

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchStates = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/states`);
      if (res.ok) setStates(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      // Fetch Professional CV
      const proRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/profile?type=professional`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (proRes.ok) {
        const data = await proRes.json();
        if (data.profile) {
          skipNextAutoSave.current = true;
          setProfessionalCv(prev => ({
            ...getInitialProfessionalState(lang),
            ...data.profile,
            cv_type: 'professional',
            employment: data.profile.employment || data.profile.experience || [],
            certifications: (data.profile.certifications || []).map(cert => ({
              name: cert.name || '',
              issuer: cert.issuer || '',
              startDate: cert.startDate || cert.startMonth || '',
              endDate: cert.endDate || cert.endMonth || ''
            })),
            references: Array.isArray(data.profile.references) 
              ? data.profile.references 
              : (data.profile.references ? [{ fullName: '', institution: '', post: '', address: '', email: '', phone: '', notes: data.profile.references }] : []),
            personal: { 
              ...getInitialProfessionalState(lang).personal, 
              ...data.profile.personal,
              portfolio: undefined
            }
          }));
          setLastSavedAt(Date.now());
        }
      }
      
      // Fetch Normal CV
      const normalRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/profile?type=normal`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (normalRes.ok) {
        const data = await normalRes.json();
        if (data.profile) {
          setNormalCv(prev => ({
            ...getInitialNormalState(lang),
            ...data.profile,
            cv_type: 'normal',
            experience: data.profile.experience || data.profile.employment || [],
            personal: { 
              ...getInitialNormalState(lang).personal, 
              ...data.profile.personal
            }
          }));
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/pricing`);
      if (res.ok) {
        const data = await res.json();
        setPricing(data);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (!user || !token) {
      navigate('/welcome');
      return;
    }
    fetchStates();
    fetchProfile();
    fetchPricing();
    fetchAutofill();
    // Fetch streak data
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/streak`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : { days: 0 })
      .then(d => setStreak(d))
      .catch(() => {});
    // Fetch pro access status
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv/unlocks`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : { unlocks: [] })
      .then(d => {
        setHasProAccess((d.unlocks || []).some(u => u.item_id === 'premium_templates'));
        const pro = (d.available_items || []).find(i => i.id === 'premium_templates');
        if (pro && typeof pro.price_coins === 'number') setProPrice(pro.price_coins);
      })
      .catch(() => {});
  }, [user, token, navigate]);

  // ============================================
  // STUDENT PROFILE AUTOFILL
  // ============================================

  const fetchAutofill = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/autofill`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAutofillData(data);
      }
    } catch (e) { console.error(e); }
  };

  // Apply autofill after profile loads (only for empty fields)
  useEffect(() => {
    if (!autofillData || autofillApplied.current || loading) return;
    autofillApplied.current = true;

    const applyToCv = (prevCv) => {
      const updated = { ...prevCv };
      const p = { ...updated.personal };

      // Autofill personal fields only if empty
      if (!p.full_name && autofillData.name) p.full_name = autofillData.name;
      if (!p.email && autofillData.email) p.email = autofillData.email;
      updated.personal = p;

      // Autofill education if empty and university available
      if ((!updated.education || updated.education.length === 0) && autofillData.university) {
        updated.education = [{
          institution: autofillData.university,
          degree: '',
          major: autofillData.faculty || '',
          start: '',
          end: '',
        }];
      }

      // Autofill skills if empty
      if ((!updated.skills || updated.skills.length === 0) && autofillData.skills?.length > 0) {
        updated.skills = [...autofillData.skills];
      }

      return updated;
    };

    setProfessionalCv(prev => applyToCv(prev));
    setNormalCv(prev => applyToCv(prev));
    skipNextAutoSave.current = true;
  }, [autofillData, loading]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updatePersonal = (field, value) => {
    setCv(prev => ({
      ...prev,
      personal: { ...prev.personal, [field]: value }
    }));
  };

  // Employment handlers
  const addEmployment = () => {
    setCv(prev => ({
      ...prev,
      employment: [...prev.employment, {
        title: '',
        company: '',
        state_key: '',
        start: '',
        end: '',
        bullets: []
      }]
    }));
    setExpandedSections(prev => ({ ...prev, employment: true }));
  };

  const updateEmployment = (index, field, value) => {
    setCv(prev => ({
      ...prev,
      employment: prev.employment.map((emp, i) => 
        i === index ? { ...emp, [field]: value } : emp
      )
    }));
  };

  const removeEmployment = (index) => {
    setCv(prev => ({
      ...prev,
      employment: prev.employment.filter((_, i) => i !== index)
    }));
  };

  const addEmpBullet = (empIndex) => {
    setCv(prev => ({
      ...prev,
      employment: prev.employment.map((emp, i) => 
        i === empIndex ? { ...emp, bullets: [...(emp.bullets || []), ''] } : emp
      )
    }));
  };

  const updateEmpBullet = (empIndex, bulletIndex, value) => {
    setCv(prev => ({
      ...prev,
      employment: prev.employment.map((emp, i) => 
        i === empIndex ? {
          ...emp,
          bullets: (emp.bullets || []).map((b, bi) => bi === bulletIndex ? value : b)
        } : emp
      )
    }));
  };

  const removeEmpBullet = (empIndex, bulletIndex) => {
    setCv(prev => ({
      ...prev,
      employment: prev.employment.map((emp, i) => 
        i === empIndex ? {
          ...emp,
          bullets: (emp.bullets || []).filter((_, bi) => bi !== bulletIndex)
        } : emp
      )
    }));
  };

  // Education handlers
  const addEducation = () => {
    setCv(prev => ({
      ...prev,
      education: [...prev.education, {
        institution: '',
        degree: '',
        major: '',
        start: '',
        end: ''
      }]
    }));
    setExpandedSections(prev => ({ ...prev, education: true }));
  };

  const updateEducation = (index, field, value) => {
    setCv(prev => ({
      ...prev,
      education: prev.education.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index) => {
    setCv(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  // Skills handlers
  const addSkill = () => {
    if (skillInput.trim()) {
      setCv(prev => ({
        ...prev,
        skills: [...(prev.skills || []), skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (index) => {
    setCv(prev => ({
      ...prev,
      skills: (prev.skills || []).filter((_, i) => i !== index)
    }));
  };

  // ============================================
  // EXPERIENCE HANDLERS (Normal Resume - simpler than Employment)
  // ============================================
  
  const addExperience = () => {
    setCv(prev => ({
      ...prev,
      experience: [...(prev.experience || []), {
        title: '',
        company: '',
        start: '',
        end: '',
        notes: '' // Single textarea, not bullet points
      }]
    }));
    setExpandedSections(prev => ({ ...prev, experience: true }));
  };

  const updateExperience = (index, field, value) => {
    setCv(prev => ({
      ...prev,
      experience: (prev.experience || []).map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const removeExperience = (index) => {
    setCv(prev => ({
      ...prev,
      experience: (prev.experience || []).filter((_, i) => i !== index)
    }));
  };

  // Check if experience entry is non-empty
  const isNonEmptyExperience = (exp) => {
    return Boolean(
      (exp?.title || '').trim() ||
      (exp?.company || '').trim() ||
      (exp?.notes || '').trim()
    );
  };

  // Languages handlers
  const addLanguage = () => {
    setCv(prev => ({
      ...prev,
      languages: [...(prev.languages || []), { name: '', level: 'intermediate' }]
    }));
    setExpandedSections(prev => ({ ...prev, languages: true }));
  };

  const updateLanguage = (index, field, value) => {
    setCv(prev => ({
      ...prev,
      languages: (prev.languages || []).map((lang, i) => 
        i === index ? { ...lang, [field]: value } : lang
      )
    }));
  };

  const removeLanguage = (index) => {
    setCv(prev => ({
      ...prev,
      languages: (prev.languages || []).filter((_, i) => i !== index)
    }));
  };

  // Certifications handlers
  const addCertification = () => {
    setCv(prev => ({
      ...prev,
      certifications: [...(prev.certifications || []), { 
        name: '', 
        issuer: '', 
        startDate: '', 
        endDate: '' 
      }]
    }));
    setExpandedSections(prev => ({ ...prev, certifications: true }));
  };

  const updateCertification = (index, field, value) => {
    setCv(prev => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) => 
        i === index ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const removeCertification = (index) => {
    setCv(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  // References handlers (NEW - structured fields)
  const addReference = () => {
    setCv(prev => ({
      ...prev,
      references: [...(prev.references || []), emptyReference()]
    }));
    setExpandedSections(prev => ({ ...prev, references: true }));
  };

  const updateReference = (index, newRef) => {
    setCv(prev => ({
      ...prev,
      references: (prev.references || []).map((ref, i) => 
        i === index ? newRef : ref
      )
    }));
  };

  const removeReference = (index) => {
    setCv(prev => ({
      ...prev,
      references: (prev.references || []).filter((_, i) => i !== index)
    }));
  };

  // ============================================
  // SAVE / EXPORT HANDLERS
  // ============================================

  // DIAG: every call site of the generic red error toast gets a unique code so
  // the exact firing path is identifiable on-device. Behavior unchanged when
  // the diagnostic build flag is off (message identical to before).
  const cvErrToast = (code, err, overrideMsg) => {
    diag('CV_TOAST_' + code, {
      name: err?.name, msg: (err?.message || overrideMsg || '').slice(0, 200),
      top: String(err?.stack || '').split('\n').slice(0, 2).join(' | ').slice(0, 200),
      exporting: localExporting,
    });
    const base = overrideMsg || t('cvBuilder.messages.error');
    toast.error(DIAG_ENABLED ? `[${code}] ${base}` : base);
  };

  const handleSave = async () => {
    // Use permissive draft validation - only requires Full Name
    const validationErrors = validateDraft(cv, t);
    
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0].message, {
        description: validationErrors.length > 1 
          ? t('cvBuilder.validation.moreErrors', { count: validationErrors.length - 1 })
          : undefined
      });
      scrollToField(validationErrors[0].field);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...cv,
        cv_type: selectedType,
        template_id: selectedType,
        experience: selectedType === 'normal' ? cv.experience : cv.employment,
        employment: selectedType === 'professional' ? cv.employment : undefined,
        status: 'draft'
      };
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/profile?type=${selectedType}&manual=true`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      let responseData = null;
      try {
        responseData = await res.json();
      } catch (e) {}

      if (res.ok) {
        toast.success(t('cvBuilder.messages.saved'));
        setIsDirty(false);
        setLastSavedAt(Date.now());
        // Update streak from response
        if (responseData?.streak) {
          setStreak(responseData.streak);
          if (responseData.streak.coin_reward && !COINS_DISABLED) {
            toast.success(
              isRTL
                ? `+${responseData.streak.coin_reward} عملات مكافأة استمرار!`
                : `+${responseData.streak.coin_reward} coins streak reward!`,
              { duration: 3000 }
            );
          }
        }
        try {
          const draftKey = getDraftKey(user.user_id, selectedType);
          localStorage.removeItem(draftKey);
        } catch (e) {}
      } else {
        if (responseData?.errors?.length > 0) {
          const firstError = responseData.errors[0];
          cvErrToast('CV_ERR_01', null, firstError.message || undefined);
        } else if (responseData?.detail) {
          const detail = responseData.detail;
          if (typeof detail === 'string') {
            toast.error(detail);
          } else {
            cvErrToast('CV_ERR_02');
          }
        } else {
          cvErrToast('CV_ERR_03', null, responseData?.message || undefined);
        }
      }
    } catch (e) {
      console.error('Save CV error:', e);
      toast.error(t('cvBuilder.messages.networkError'));
    }
    setSaving(false);
  };

  const handlePreview = async () => {
    // Use permissive draft validation for preview
    const validationErrors = validateDraft(cv, t);
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0].message);
      scrollToField(validationErrors[0].field);
      return;
    }

    // Show local preview modal - no API required
    setShowPreviewModal(true);
  };

  const handleExport = async () => {
    // Use STRICT validation for final export
    const validationErrors = validateFinal(cv, t);
    if (validationErrors.length > 0) {
      toast.error(t('cvBuilder.validation.exportRequiresComplete'), {
        description: validationErrors[0].message
      });
      scrollToField(validationErrors[0].field);
      return;
    }

    // Clean export is a CV Pro benefit — no per-export coin charge and no
    // client-side balance guessing. Not Pro yet → open the unlock modal.
    if (!hasProAccess) {
      setShowUnlockModal(true);
      return;
    }

    setExporting(true);
    try {
      const payload = {
        ...cv,
        cv_type: selectedType,
        template_id: selectedType,
        experience: selectedType === 'normal' ? cv.experience : cv.employment,
        employment: selectedType === 'professional' ? cv.employment : undefined,
        status: 'final'
      };
      
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/profile?type=${selectedType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/export?preview=false&type=${selectedType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        const byteCharacters = atob(data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = data.filename || 'cv.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(t('cvBuilder.messages.exported'));
        setShowPaymentModal(false);
      } else if (res.status === 402) {
        // Server says Pro isn't owned (or was revoked) — open the unlock modal.
        setShowUnlockModal(true);
      } else {
        cvErrToast('CV_ERR_04', { name: 'HTTP', message: 'export status ' + res.status });
      }
    } catch (e) {
      console.error(e);
      cvErrToast('CV_ERR_05', e);
    }
    setExporting(false);
  };

  const handleBankakRequest = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-builder/request-bankak-export`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(t('cvBuilder.messages.bankakRequested'));
      } else {
        cvErrToast('CV_ERR_06', { name: 'HTTP', message: 'bankak status ' + res.status });
      }
    } catch (e) {
      console.error(e);
      cvErrToast('CV_ERR_07', e);
    }
  };

  const downloadPreviewPdf = () => {
    if (!previewPdf) return;
    const byteCharacters = atob(previewPdf);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  // Free download: opens the preview with watermark for the user to print/save
  const handleFreeDownload = () => {
    setShowUnlockModal(false);
    setShowPreviewModal(true);
    toast.success(t('cvBuilder.actions.preview'));
  };

  // Pro download: uses coins to export clean PDF
  // Purchase the Pro package directly — decoupled from export so users can
  // unlock from any entry point even with an empty CV draft (iter383).
  const handleUnlockProPurchase = async () => {
    if (unlockingPro) return;
    setUnlockingPro(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ item_type: 'template_pack', item_id: 'premium_templates' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.success || data.already_unlocked)) {
        setHasProAccess(true);
        if (typeof data.coins_remaining === 'number') updateUser({ coins: data.coins_remaining });
        setShowUnlockModal(false);
        toast.success(isRTL ? 'تم تفعيل باقة Pro بنجاح' : 'CV Pro unlocked successfully');
      } else if (res.status === 402) {
        toast.error(isRTL ? 'لا توجد عملات كافية' : 'Not enough coins');
      } else {
        toast.error(isRTL ? 'تعذر إتمام العملية، حاول مجدداً' : 'Could not complete the purchase, try again');
      }
    } catch (_e) {
      toast.error(isRTL ? 'خطأ في الاتصال' : 'Network error');
    }
    setUnlockingPro(false);
  };

  const handleProDownload = async () => {
    setShowUnlockModal(false);
    await handleExport();
  };

  // Unified download: routes to free (watermark) or pro (clean) based on access
  const handleDownloadCV = () => {
    const errors = validateFinal(cv, t);
    if (errors.length > 0) {
      toast.error(t('cvBuilder.validation.exportRequiresComplete'), { description: errors[0].message });
      scrollToField(errors[0].field);
      return;
    }
    if (COINS_DISABLED) {
      // Android build: clean preview → free on-device PDF (no backend call)
      setShowPreviewModal(true);
    } else if (hasProAccess) {
      handleExport();
    } else {
      // Free path: open preview with watermark for print/save
      setShowPreviewModal(true);
    }
  };

  // Android build: generate the final PDF locally from the rendered preview
  // DOM — free, clean, entirely on device. Never touches the paid endpoint.
  const handleLocalPdfExport = async () => {
    const el = document.getElementById('cv-print-root');
    if (!el || localExporting) return;
    setLocalExporting(true);
    diag('EXPORT_TAP');
    try {
      const name = (cv.personal?.full_name || 'cv').trim().replace(/\s+/g, '_');
      const result = await exportCvElementToPdf(el, `${name}_CV.pdf`);
      diag('EXPORT_DONE', result);
      // Deterministic outcomes: saved → success toast; user cancelled the
      // Save dialog → silent normal return; ONLY a genuine generation/write
      // failure reaches the catch below.
      if (result?.saved) {
        toast.success(t('cvBuilder.messages.pdfSaved'));
      } else if (result?.shared) {
        toast.success(t('cvBuilder.messages.exported'));
      }
    } catch (e) {
      console.error('Local CV export failed:', e);
      diag('EXPORT_FAILED', { msg: (e?.message || '').slice(0, 200) });
      toast.error(t('cvBuilder.messages.pdfError'));
    } finally {
      setLocalExporting(false);
    }
  };

  // ============================================
  // RENDER SECTION HELPER
  // ============================================

  const renderSection = (sectionConfig) => {
    const { id, titleKey, icon: Icon } = sectionConfig;
    
    switch (id) {
      case 'summary':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.summary}
            onToggle={() => toggleSection('summary')}
            testId="section-summary"
            isComplete={sectionComplete.summary}
          >
            <Textarea
              value={cv.summary}
              onChange={(e) => setCv(prev => ({ ...prev, summary: e.target.value }))}
              placeholder={t('cvBuilder.fields.summary')}
              rows={4}
              data-testid="cv-summary"
            />
          </SectionCard>
        );
      
      case 'personal':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.personal}
            onToggle={() => toggleSection('personal')}
            testId="section-personal"
            isComplete={sectionComplete.personal}
          >
            <div className="form-grid">
              <div className="form-group">
                <Label>{t('cvBuilder.fields.fullName')} *</Label>
                <Input
                  value={cv.personal.full_name}
                  onChange={(e) => updatePersonal('full_name', e.target.value)}
                  placeholder={t('cvBuilder.fields.fullName')}
                  data-testid="cv-fullname"
                  data-field="personal.full_name"
                />
              </div>
              <div className="form-group">
                <Label>{t('cvBuilder.fields.email')}</Label>
                <Input
                  type="email"
                  value={cv.personal.email}
                  onChange={(e) => updatePersonal('email', e.target.value)}
                  placeholder={t('cvBuilder.fields.email')}
                  data-testid="cv-email"
                  data-field="personal.email"
                />
              </div>
              <div className="form-group">
                <Label>{t('cvBuilder.fields.phone')}</Label>
                {(cv.personal.phones || [cv.personal.phone || '']).map((ph, idx) => (
                  <div key={idx} className="repeatable-row" data-testid={`cv-phone-${idx}`}>
                    <Input
                      value={ph}
                      onChange={(e) => {
                        const arr = [...(cv.personal.phones || [cv.personal.phone || ''])];
                        arr[idx] = e.target.value;
                        setCv(prev => ({ ...prev, personal: { ...prev.personal, phones: arr, phone: arr[0] || '' } }));
                      }}
                      placeholder={t('cvBuilder.fields.phone')}
                      data-testid={idx === 0 ? "cv-phone" : `cv-phone-${idx}`}
                    />
                    {idx > 0 && (
                      <Button variant="ghost" size="sm" className="remove-btn-inline" onClick={() => {
                        const arr = [...(cv.personal.phones || [])];
                        arr.splice(idx, 1);
                        setCv(prev => ({ ...prev, personal: { ...prev.personal, phones: arr, phone: arr[0] || '' } }));
                      }}><Trash2 size={14} /></Button>
                    )}
                  </div>
                ))}
                <button className="add-another-btn" data-testid="add-phone" onClick={() => {
                  const arr = [...(cv.personal.phones || [cv.personal.phone || '']), ''];
                  setCv(prev => ({ ...prev, personal: { ...prev.personal, phones: arr } }));
                }}>
                  <Plus size={14} /> {isRTL ? 'أضف رقم آخر' : 'Add another'}
                </button>
              </div>
              <div className="form-group">
                <Label>{t('cvBuilder.fields.state')}</Label>
                <CityInput
                  value={cv.personal.city}
                  onChange={(v) => setCv(prev => ({ ...prev, personal: { ...prev.personal, city: v } }))}
                  states={states}
                  lang={lang}
                />
              </div>
              {/* Links - Professional only */}
              {selectedType === 'professional' && (
                <div className="form-group full-width">
                  <Label>{isRTL ? 'الروابط' : 'Links'}</Label>
                  {(cv.personal.links || []).map((link, idx) => (
                    <div key={idx} className="repeatable-row link-row" data-testid={`cv-link-${idx}`}>
                      <Select value={link.label || 'LinkedIn'} onValueChange={(v) => {
                        const arr = [...(cv.personal.links || [])];
                        arr[idx] = { ...arr[idx], label: v };
                        setCv(prev => ({ ...prev, personal: { ...prev.personal, links: arr, linkedin: arr.find(l => l.label === 'LinkedIn')?.url || '' } }));
                      }}>
                        <SelectTrigger className="link-label-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LINK_LABELS.map(l => (
                            <SelectItem key={l.value} value={l.value}>{isRTL ? l.ar : l.en}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={link.url || ''}
                        onChange={(e) => {
                          const arr = [...(cv.personal.links || [])];
                          arr[idx] = { ...arr[idx], url: e.target.value };
                          setCv(prev => ({ ...prev, personal: { ...prev.personal, links: arr, linkedin: arr.find(l => l.label === 'LinkedIn')?.url || '' } }));
                        }}
                        placeholder="https://..."
                        className="link-url-input"
                      />
                      {idx > 0 && (
                        <Button variant="ghost" size="sm" className="remove-btn-inline" onClick={() => {
                          const arr = [...(cv.personal.links || [])];
                          arr.splice(idx, 1);
                          setCv(prev => ({ ...prev, personal: { ...prev.personal, links: arr, linkedin: arr.find(l => l.label === 'LinkedIn')?.url || '' } }));
                        }}><Trash2 size={14} /></Button>
                      )}
                    </div>
                  ))}
                  <button className="add-another-btn" data-testid="add-link" onClick={() => {
                    const arr = [...(cv.personal.links || []), { label: 'Other', url: '' }];
                    setCv(prev => ({ ...prev, personal: { ...prev.personal, links: arr } }));
                  }}>
                    <Plus size={14} /> {isRTL ? 'أضف رابط آخر' : 'Add another link'}
                  </button>
                </div>
              )}
            </div>
          </SectionCard>
        );
      
      // OBJECTIVE - Normal Resume only (simpler than professional summary)
      case 'objective':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.objective}
            onToggle={() => toggleSection('objective')}
            testId="section-objective"
            isComplete={sectionComplete.objective}
          >
            <Textarea
              value={cv.objective || ''}
              onChange={(e) => setCv(prev => ({ ...prev, objective: e.target.value }))}
              placeholder={t('cvBuilder.fields.objective')}
              rows={3}
              data-testid="cv-objective"
            />
          </SectionCard>
        );
      
      case 'employment':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.employment}
            onToggle={() => toggleSection('employment')}
            count={countNonEmpty(cv.employment, isNonEmptyEmployment)}
            testId="section-employment"
            isComplete={sectionComplete.employment}
          >
            {cv.employment.map((emp, index) => (
              <div key={index} className="list-item">
                <div className="item-header">
                  <span className="item-number">{index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEmployment(index)}
                    className="remove-btn"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.jobTitle')}</Label>
                    <Input
                      value={emp.title}
                      onChange={(e) => updateEmployment(index, 'title', e.target.value)}
                      placeholder={t('cvBuilder.fields.jobTitle')}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.company')}</Label>
                    <Input
                      value={emp.company}
                      onChange={(e) => updateEmployment(index, 'company', e.target.value)}
                      placeholder={t('cvBuilder.fields.company')}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.startDate')}</Label>
                    <DateInput
                      value={emp.start}
                      onChange={(v) => updateEmployment(index, 'start', v)}
                    />
                    {emp.start && new Date(emp.start) > new Date() && (
                      <span className="field-error" data-testid={`emp-${index}-start-error`}>
                        {isRTL ? 'تاريخ البداية لا يمكن أن يكون في المستقبل' : 'Start date cannot be in the future'}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="current-job-check" data-testid={`emp-${index}-current-job`}>
                      <input
                        type="checkbox"
                        checked={!!emp.currentJob}
                        onChange={(e) => {
                          updateEmployment(index, 'currentJob', e.target.checked);
                          if (e.target.checked) updateEmployment(index, 'end', '');
                        }}
                      />
                      <span>{isRTL ? 'أعمل هنا حالياً' : 'Currently working here'}</span>
                    </label>
                    {!emp.currentJob && (
                      <>
                        <Label>{t('cvBuilder.fields.endDate')}</Label>
                        <DateInput
                          value={emp.end}
                          onChange={(v) => updateEmployment(index, 'end', v)}
                        />
                        {emp.start && emp.end && new Date(emp.end) < new Date(emp.start) && (
                          <span className="field-error" data-testid={`emp-${index}-end-error`}>
                            {isRTL ? 'تاريخ النهاية لا يمكن أن يكون قبل تاريخ البداية' : 'End date cannot be before start date'}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="bullets-section">
                  <Label>{t('cvBuilder.fields.bullets')}</Label>
                  {(emp.bullets || []).map((bullet, bi) => (
                    <div key={bi} className="bullet-row">
                      <Input
                        value={bullet}
                        onChange={(e) => updateEmpBullet(index, bi, e.target.value)}
                        placeholder="..."
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEmpBullet(index, bi)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addEmpBullet(index)}
                    className="add-bullet-btn"
                  >
                    <Plus size={14} /> {t('cvBuilder.fields.addBullet')}
                  </Button>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={addEmployment}
              className="add-item-btn"
              data-testid="add-employment"
            >
              <Plus size={16} /> {t('cvBuilder.actions.addEmployment')}
            </Button>
          </SectionCard>
        );
      
      // EXPERIENCE - Normal Resume only (simpler than Employment)
      case 'experience':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.experience}
            onToggle={() => toggleSection('experience')}
            count={countNonEmpty(cv.experience || [], isNonEmptyExperience)}
            testId="section-experience"
            isComplete={sectionComplete.experience}
          >
            {(cv.experience || []).map((exp, index) => (
              <div key={index} className="list-item">
                <div className="item-header">
                  <span className="item-number">{index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeExperience(index)}
                    className="remove-btn"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.jobTitle')}</Label>
                    <Input
                      value={exp.title || ''}
                      onChange={(e) => updateExperience(index, 'title', e.target.value)}
                      placeholder={t('cvBuilder.fields.jobTitle')}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.company')}</Label>
                    <Input
                      value={exp.company || ''}
                      onChange={(e) => updateExperience(index, 'company', e.target.value)}
                      placeholder={t('cvBuilder.fields.company')}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.startDate')}</Label>
                    <DateInput
                      value={exp.start || ''}
                      onChange={(v) => updateExperience(index, 'start', v)}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.endDate')}</Label>
                    <DateInput
                      value={exp.end || ''}
                      onChange={(v) => updateExperience(index, 'end', v)}
                    />
                  </div>
                  <div className="form-group full-width">
                    <Label>{t('cvBuilder.fields.notes')}</Label>
                    <Textarea
                      value={exp.notes || ''}
                      onChange={(e) => updateExperience(index, 'notes', e.target.value)}
                      placeholder={t('cvBuilder.fields.notesPlaceholder')}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={addExperience}
              className="add-item-btn"
              data-testid="add-experience"
            >
              <Plus size={16} /> {t('cvBuilder.actions.addExperience')}
            </Button>
          </SectionCard>
        );
      
      case 'education':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.education}
            onToggle={() => toggleSection('education')}
            count={countNonEmpty(cv.education, isNonEmptyEducation)}
            testId="section-education"
            isComplete={sectionComplete.education}
          >
            {cv.education.map((edu, index) => (
              <div key={index} className="list-item">
                <div className="item-header">
                  <span className="item-number">{index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEducation(index)}
                    className="remove-btn"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.institution')}</Label>
                    <Input
                      value={edu.institution}
                      onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                      placeholder={t('cvBuilder.fields.institution')}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.degree')}</Label>
                    <DegreeSelect
                      value={edu.degree}
                      onChange={(v) => updateEducation(index, 'degree', v)}
                      lang={lang}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.major')}</Label>
                    <Input
                      value={edu.major}
                      onChange={(e) => updateEducation(index, 'major', e.target.value)}
                      placeholder={t('cvBuilder.fields.major')}
                    />
                  </div>
                  <div className="form-group two-col">
                    <div>
                      <Label>{t('cvBuilder.fields.startDate')}</Label>
                      <DateInput
                        value={edu.start}
                        onChange={(v) => updateEducation(index, 'start', v)}
                      />
                    </div>
                    <div>
                      <Label>{t('cvBuilder.fields.endDate')}</Label>
                      <DateInput
                        value={edu.end}
                        onChange={(v) => updateEducation(index, 'end', v)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={addEducation}
              className="add-item-btn"
              data-testid="add-education"
            >
              <Plus size={16} /> {t('cvBuilder.actions.addEducation')}
            </Button>
          </SectionCard>
        );
      
      case 'skills':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.skills}
            onToggle={() => toggleSection('skills')}
            count={cv.skills.length}
            testId="section-skills"
            isComplete={sectionComplete.skills}
          >
            <div className="skills-input-row">
              <Input
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                placeholder={t('cvBuilder.fields.skill')}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                data-testid="skill-input"
              />
              <Button onClick={addSkill} variant="outline" data-testid="add-skill">
                <Plus size={16} />
              </Button>
            </div>
            <div className="skills-tags">
              {cv.skills.map((skill, index) => (
                <Badge key={index} variant="secondary" className="skill-tag">
                  {skill}
                  <button onClick={() => removeSkill(index)} className="remove-skill">
                    <Trash2 size={12} />
                  </button>
                </Badge>
              ))}
            </div>
          </SectionCard>
        );
      
      case 'languages':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.languages}
            onToggle={() => toggleSection('languages')}
            count={countNonEmpty(cv.languages, isNonEmptyLanguage)}
            testId="section-languages"
            isComplete={sectionComplete.languages}
          >
            {cv.languages.map((langItem, index) => (
              <div key={index} className="list-item compact">
                <div className="item-header">
                  <span className="item-number">{index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLanguage(index)}
                    className="remove-btn"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.language')}</Label>
                    <Input
                      value={langItem.name}
                      onChange={(e) => updateLanguage(index, 'name', e.target.value)}
                      placeholder={t('cvBuilder.fields.language')}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{isRTL ? 'المستوى' : 'Level'}</Label>
                    <Select 
                      value={langItem.level} 
                      onValueChange={(v) => updateLanguage(index, 'level', v)}
                    >
                      <SelectTrigger className="level-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="native">{t('cvBuilder.levels.native')}</SelectItem>
                        <SelectItem value="fluent">{t('cvBuilder.levels.fluent')}</SelectItem>
                        <SelectItem value="advanced">{t('cvBuilder.levels.advanced')}</SelectItem>
                        <SelectItem value="intermediate">{t('cvBuilder.levels.intermediate')}</SelectItem>
                        <SelectItem value="basic">{t('cvBuilder.levels.basic')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={addLanguage}
              className="add-item-btn"
              data-testid="add-language"
            >
              <Plus size={16} /> {t('cvBuilder.actions.addLanguage')}
            </Button>
          </SectionCard>
        );
      
      case 'certifications':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.certifications}
            onToggle={() => toggleSection('certifications')}
            count={countNonEmpty(cv.certifications, isNonEmptyCertification)}
            testId="section-certifications"
            isComplete={sectionComplete.certifications}
          >
            {cv.certifications.map((cert, index) => (
              <div key={index} className="list-item compact">
                <div className="item-header">
                  <span className="item-number">{index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCertification(index)}
                    className="remove-btn"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.certName')}</Label>
                    <Input
                      value={cert.name}
                      onChange={(e) => updateCertification(index, 'name', e.target.value)}
                      placeholder={t('cvBuilder.fields.certName')}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.issuer')}</Label>
                    <Input
                      value={cert.issuer}
                      onChange={(e) => updateCertification(index, 'issuer', e.target.value)}
                      placeholder={t('cvBuilder.fields.issuer')}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.certStart')}</Label>
                    <DateInput
                      value={cert.startDate}
                      onChange={(v) => updateCertification(index, 'startDate', v)}
                    />
                  </div>
                  <div className="form-group">
                    <Label>{t('cvBuilder.fields.certEnd')}</Label>
                    <DateInput
                      value={cert.endDate}
                      onChange={(v) => updateCertification(index, 'endDate', v)}
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={addCertification}
              className="add-item-btn"
              data-testid="add-certification"
            >
              <Plus size={16} /> {t('cvBuilder.actions.addCertification')}
            </Button>
          </SectionCard>
        );
      
      case 'references':
        return (
          <SectionCard
            key={id}
            title={t(titleKey)}
            icon={<Icon size={20} />}
            expanded={expandedSections.references}
            onToggle={() => toggleSection('references')}
            count={countNonEmpty(cv.references || [], isNonEmptyReference)}
            testId="section-references"
            isComplete={sectionComplete.references}
          >
            {(cv.references || []).map((refItem, index) => (
              <ReferenceForm
                key={index}
                refItem={refItem}
                index={index}
                onChange={(newRef) => updateReference(index, newRef)}
                onRemove={() => removeReference(index)}
                t={t}
              />
            ))}
            <Button
              variant="outline"
              onClick={addReference}
              className="add-item-btn"
              data-testid="add-reference"
            >
              <Plus size={16} /> {t('cvBuilder.actions.addReference')}
            </Button>
          </SectionCard>
        );
      
      default:
        return null;
    }
  };

  // ============================================
  // PUBLIC CV LINK - FETCH USERNAME
  // ============================================

  useEffect(() => {
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv/my-username`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).then(d => {
        if (d.username) { setPublicUsername(d.username); setUsernameInput(d.username); }
      }).catch(() => {});
    }
  }, [token]);

  // ============================================
  // LOADING STATE
  // ============================================

  if (loading) {
    return (
      <div className="cv-builder-page" dir={isRTL ? 'rtl' : 'ltr'}>
        <AppHeader
          variant="indigo"
          title={t('cvBuilder.title')}
          subtitle={t('cvBuilder.subtitle')}
          backFallback="/student-hub"
          testId="cv-header"
        />
        <div className="cv-content" style={{ padding: '1rem' }}>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const exportCost = proPrice; // CV Pro package price (server catalog)
  const canSave = isCvSavable(cv);

  // Section completion map — used by SectionCard indicators and strength chips
  const sectionComplete = {
    summary: !!(cv.summary && cv.summary.trim().length > 10),
    personal: !!(cv.personal?.full_name?.trim() && cv.personal?.email?.trim()),
    employment: (cv.employment || []).some(e => e.title?.trim() && e.company?.trim()),
    education: (cv.education || []).some(e => e.institution?.trim()),
    skills: (cv.skills || []).length >= 1 && (cv.skills || []).some(s => typeof s === 'string' ? s.trim() : s?.name?.trim()),
    languages: (cv.languages || []).length >= 1 && (cv.languages || []).some(l => typeof l === 'string' ? l.trim() : l?.name?.trim()),
    certifications: (cv.certifications || []).some(c => c.name?.trim()),
    references: (cv.references || []).some(r => r.name?.trim()),
    objective: !!(cv.objective && cv.objective.trim().length > 5),
    experience: (cv.experience || []).some(e => e.title?.trim()),
  };

  // ============================================
  // CV STRENGTH SCORE — core sections = 100%, references = bonus
  // ============================================

  const coreIds = Object.keys(CV_SCORING.core);
  const bonusIds = Object.keys(CV_SCORING.bonus);

  const coreTotal = currentSections
    .filter(s => coreIds.includes(s.id) || CV_SCORING.aliases[s.id])
    .reduce((sum, sec) => {
      const resolvedId = CV_SCORING.aliases[sec.id] || sec.id;
      return sum + (CV_SCORING.core[resolvedId] || 0);
    }, 0);

  const coreCompleted = currentSections
    .filter(s => coreIds.includes(s.id) || CV_SCORING.aliases[s.id])
    .reduce((sum, sec) => {
      const resolvedId = CV_SCORING.aliases[sec.id] || sec.id;
      return sectionComplete[sec.id] ? sum + (CV_SCORING.core[resolvedId] || 0) : sum;
    }, 0);

  const bonusCompleted = currentSections
    .filter(s => bonusIds.includes(s.id))
    .reduce((sum, sec) => sectionComplete[sec.id] ? sum + (CV_SCORING.bonus[sec.id] || 0) : sum, 0);

  const rawPct = coreTotal > 0 ? Math.round((coreCompleted / coreTotal) * 100) : 0;
  const bonusPct = bonusCompleted; // bonus is already in percentage points
  const cvStrength = { pct: Math.min(rawPct + bonusPct, 100), corePct: rawPct, bonus: bonusPct };

  // Scroll to a section and expand it
  const scrollToSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="section-${sectionId}"]`);
      if (el) {
        const container = document.querySelector('.main-content') || window;
        const offsetTop = el.getBoundingClientRect().top + (container.scrollTop || 0) - 140;
        container.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    }, 150);
  };

  // Incomplete sections with their weights (for "Improve" suggestions)
  const missingSections = currentSections.filter(s => !sectionComplete[s.id]);

  // Next Step Highlight — highest-weight incomplete section
  const weightedMissing = missingSections.filter(s => (SECTION_WEIGHTS[s.id] || 0) > 0);
  const nextStep = weightedMissing.length > 0
    ? [...weightedMissing].sort((a, b) => (SECTION_WEIGHTS[b.id] || 0) - (SECTION_WEIGHTS[a.id] || 0))[0]
    : null;

  // Scroll to section with highlight animation
  const scrollToSectionHighlight = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="section-${sectionId}"]`);
      if (el) {
        const container = document.querySelector('.main-content') || window;
        const offsetTop = el.getBoundingClientRect().top + (container.scrollTop || 0) - 140;
        container.scrollTo({ top: offsetTop, behavior: 'smooth' });
        el.classList.add('section-highlight-flash');
        setTimeout(() => el.classList.remove('section-highlight-flash'), 1800);
      }
    }, 150);
  };

  // Section display names (EN / AR)
  const sectionNames = {
    summary: { en: 'Summary', ar: 'الملخص' },
    personal: { en: 'Personal Info', ar: 'الشخصية' },
    employment: { en: 'Work Experience', ar: 'الخبرة المهنية' },
    education: { en: 'Education', ar: 'التعليم' },
    skills: { en: 'Skills', ar: 'المهارات' },
    languages: { en: 'Languages', ar: 'اللغات' },
    certifications: { en: 'Certifications', ar: 'الشهادات' },
    references: { en: 'References', ar: 'المراجع' },
    objective: { en: 'Objective', ar: 'الهدف' },
    experience: { en: 'Experience', ar: 'الخبرة' },
  };

  // ============================================
  // PUBLIC CV LINK HANDLERS
  // ============================================

  const handleSetUsername = async () => {
    if (!usernameInput.trim() || usernameInput.trim().length < 3) return;
    setSettingUsername(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv/set-username`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: usernameInput.trim().toLowerCase() }),
      });
      if (res.ok) {
        const d = await res.json();
        setPublicUsername(d.username);
        setShowUsernameForm(false);
        toast.success(isRTL ? 'تم حفظ اسم المستخدم' : 'Username saved');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error');
      }
    } catch (e) { toast.error('Network error'); }
    setSettingUsername(false);
  };

  const copyPublicLink = () => {
    const link = buildShareUrl(`/cv/${publicUsername}`);
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    toast.success(isRTL ? 'تم نسخ الرابط' : 'Link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className="cv-builder-page" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Unlock Premium CV Modal — portaled to <body> so no ancestor stacking
          context can trap it under the fixed bottom nav / FAB */}
      {!COINS_DISABLED && showUnlockModal && createPortal(
        <div className="unlock-overlay" onClick={() => setShowUnlockModal(false)} data-testid="unlock-modal">
          <div className="unlock-modal" onClick={e => e.stopPropagation()}>
            <button className="unlock-close" onClick={() => setShowUnlockModal(false)} data-testid="unlock-close">
              <X size={20} />
            </button>
            <CoinPurchasePanel
              variant="cv"
              price={proPrice}
              loading={unlockingPro}
              success={false}
              onPurchase={handleUnlockProPurchase}
              onClose={() => setShowUnlockModal(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* CV Preview Modal */}
      {showPreviewModal && (
        <CvPreviewModal 
          cv={normalizeCv(cv)} 
          cvType={selectedType}
          onClose={() => setShowPreviewModal(false)}
          t={t}
          lang={lang}
          isRTL={isRTL}
          noWatermark={COINS_DISABLED}
          onLocalExport={COINS_DISABLED ? handleLocalPdfExport : undefined}
          localExporting={localExporting}
        />
      )}

      {/* Shared AppHeader — Material-style: back + title on inline-start,
          coin pill at inline-end. Flex layout makes overlap impossible. */}
      <AppHeader
        variant="indigo"
        title={t('cvBuilder.title')}
        subtitle={t('cvBuilder.subtitle')}
        backFallback="/student-hub"
        testId="cv-header"
        rightSlot={COINS_DISABLED ? null :
          <button
            className="cv-coin-pill"
            onClick={() => setShowCoinInfo(true)}
            data-testid="cv-coin-info-btn"
            aria-label={t('cvBuilder.unlock.coinInfoTitle')}
          >
            <span className="cv-coin-dot" />
            <span className="cv-coin-count">{user?.coins || 0}</span>
            <span className="cv-coin-plus">+</span>
          </button>
        }
      />

      {/* Coin helper hint */}
      {!COINS_DISABLED && (
      <p className="cv-coin-hint" data-testid="cv-coin-hint">
        {isRTL ? 'استخدم العملات لفتح قوالب احترافية' : 'Use coins to unlock Pro templates'}
      </p>
      )}

      {/* CV Strength Score */}
      <div className="cv-strength-bar" data-testid="cv-strength-bar">
        <div className="strength-section">
          <div className="strength-header">
            <span className="strength-label">{isRTL ? 'قوة السيرة الذاتية' : 'CV Strength'}</span>
            <span className={`strength-pct ${cvStrength.pct >= 80 ? 'high' : cvStrength.pct >= 50 ? 'mid' : 'low'}`}>{cvStrength.pct}%</span>
          </div>
          <div className="strength-track">
            <div className={`strength-fill ${cvStrength.pct >= 80 ? 'high' : cvStrength.pct >= 50 ? 'mid' : 'low'}`} style={{ width: `${cvStrength.pct}%` }} />
          </div>

          {/* Section chips with score hints */}
          <div className="strength-chips" data-testid="strength-chips">
            {currentSections.map(s => {
              const done = sectionComplete[s.id];
              const weight = SECTION_WEIGHTS[s.id] || 0;
              const name = isRTL ? (sectionNames[s.id]?.ar || s.id) : (sectionNames[s.id]?.en || s.id);
              return (
                <button
                  key={s.id}
                  className={`strength-chip ${done ? 'done' : ''}`}
                  onClick={() => !done && scrollToSection(s.id)}
                  data-testid={`chip-${s.id}`}
                >
                  {done && <Check size={10} />}
                  {name}
                  {!done && weight > 0 && <span className="chip-hint">+{weight}%</span>}
                </button>
              );
            })}
          </div>

          {/* References bonus tooltip */}
          {!sectionComplete.references && cvStrength.corePct >= 40 && (
            <p className="ref-bonus-tip" data-testid="ref-bonus-tip">
              {isRTL ? 'أضف المراجع لزيادة القوة بنسبة +5%' : 'Add References for a +5% bonus'}
            </p>
          )}

          {/* Improve your CV suggestions — only show sections with actual weight */}
          {missingSections.filter(s => (SECTION_WEIGHTS[s.id] || 0) > 0).length > 0 && (
            <div className="cv-improve-section" data-testid="cv-improve-section">
              <p className="improve-title">{isRTL ? 'حسّن سيرتك الذاتية:' : 'Improve your CV score:'}</p>
              <div className="improve-list">
                {missingSections.filter(s => (SECTION_WEIGHTS[s.id] || 0) > 0).slice(0, 4).map(s => {
                  const weight = SECTION_WEIGHTS[s.id] || 0;
                  const name = isRTL ? (sectionNames[s.id]?.ar || s.id) : (sectionNames[s.id]?.en || s.id);
                  return (
                    <button
                      key={s.id}
                      className="improve-item"
                      onClick={() => scrollToSection(s.id)}
                      data-testid={`improve-${s.id}`}
                    >
                      <Plus size={10} />
                      <span>{isRTL ? `${name} (+${weight}%)` : `Add ${name} (+${weight}%)`}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Next Step Highlight — single highest-weight incomplete section */}
          {nextStep && (
            <button
              className="cv-next-step"
              onClick={() => scrollToSectionHighlight(nextStep.id)}
              data-testid="cv-next-step"
            >
              <Star size={14} className="next-step-star" />
              <div className="next-step-content">
                <span className="next-step-label">{isRTL ? 'الخطوة التالية لتحسين سيرتك الذاتية' : 'Next step to improve your CV'}</span>
                <strong className="next-step-action">
                  {isRTL
                    ? `${sectionNames[nextStep.id]?.ar || nextStep.id} (+${SECTION_WEIGHTS[nextStep.id] || 0}%)`
                    : `Add ${sectionNames[nextStep.id]?.en || nextStep.id} (+${SECTION_WEIGHTS[nextStep.id] || 0}%)`
                  }
                </strong>
              </div>
              <ChevronRight size={16} className="next-step-arrow" />
            </button>
          )}

          {/* CV Progress Streak */}
          {streak.days > 0 && (
            <div className="cv-streak" data-testid="cv-streak">
              <span className="streak-fire">&#128293;</span>
              <div className="streak-content">
                <span className="streak-days">
                  {isRTL
                    ? `استمرار التقدم: ${streak.days} ${streak.days === 1 ? 'يوم' : 'أيام'}`
                    : `CV Progress Streak: ${streak.days} ${streak.days === 1 ? 'day' : 'days'}`
                  }
                </span>
                <span className="streak-hint">
                  {isRTL ? 'حسّن قسم واحد اليوم للحفاظ على استمرارك.' : 'Improve one section today to keep your streak.'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Helper text — what the user should do */}
      <div className="cv-helper-text" data-testid="cv-helper-text">
        <p>{t('cvBuilder.helperText')}</p>
        <span className="cv-selected-template">
          {COINS_DISABLED
            ? (isRTL ? 'السيرة الذاتية' : 'CV Builder')
            : hasProAccess
            ? (isRTL ? 'Pro' : 'Pro plan')
            : (isRTL ? 'مجاني' : 'Free plan')
          }
        </span>
      </div>

      {/* Micro guidance */}
      <p className="cv-micro-guide" data-testid="cv-micro-guide">
        {isRTL ? 'ابدأ بالملخص والمعلومات الشخصية.' : 'Start with your summary and contact information.'}
      </p>

      {/* CV Type Selector — hidden if user arrived from landing page with a plan choice */}
      {!arrivedWithPlan.current && (
        <div className="template-selector">
          <Label>{t('cvBuilder.selectType')}</Label>
          <div className="templates-grid">
            {CV_TYPES.map(cvType => {
              const IconComponent = CV_TYPE_ICONS[cvType.key] || FileText;
              return (
                <button
                  key={cvType.key}
                  className={`template-card ${selectedType === cvType.key ? 'selected' : ''}`}
                  onClick={() => handleTypeChange(cvType.key)}
                  data-testid={`cv-type-${cvType.key}`}
                >
                  <IconComponent size={24} />
                  <span>{t(cvType.labelKey)}</span>
                  {cvType.key === 'professional' && (
                    <Badge variant="secondary" className="template-badge">{t('recommended')}</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* CV Form */}
      <div className="cv-form">
        {currentSections.map(sectionConfig => renderSection(sectionConfig))}
      </div>

      {/* Public Link (moved from header — less clutter) */}
      <div className="cv-public-link-row" data-testid="cv-public-link">
        {publicUsername ? (
          <div className="public-link-display">
            <Link size={14} />
            <code className="public-url">/cv/{publicUsername}</code>
            <Button variant="ghost" size="sm" onClick={copyPublicLink} data-testid="copy-cv-link" className="copy-link-btn">
              {linkCopied ? <Check size={14} /> : <Copy size={14} />}
            </Button>
          </div>
        ) : (
          <div className="public-link-setup">
            {showUsernameForm ? (
              <div className="username-form">
                <Input
                  value={usernameInput}
                  onChange={e => setUsernameInput(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                  placeholder={isRTL ? 'اسم المستخدم' : 'username'}
                  className="username-input"
                  data-testid="cv-username-input"
                />
                <Button size="sm" onClick={handleSetUsername} disabled={settingUsername || usernameInput.trim().length < 3} data-testid="save-username-btn">
                  {settingUsername ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setShowUsernameForm(true)} className="setup-link-btn" data-testid="setup-cv-link">
                <Link size={14} /> {isRTL ? 'انشاء رابط عام' : 'Create public link'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Pro Templates — Canva-style preview for free users */}
      {!COINS_DISABLED && !hasProAccess && (
        <div className="premium-bait" data-testid="premium-template-bait">
          <h3 className="premium-bait-title">{isRTL ? 'نماذج Pro' : 'Pro Templates'}</h3>
          <div className="premium-bait-grid">
            {[
              { id: 'modern', label: isRTL ? 'عصري' : 'Modern' },
              { id: 'executive', label: isRTL ? 'تنفيذي' : 'Executive' },
            ].map(tmpl => (
              <button
                key={tmpl.id}
                className="premium-bait-card"
                onClick={() => setShowProPreview(tmpl)}
                data-testid={`premium-bait-${tmpl.id}`}
              >
                <div className="bait-thumb">
                  <div className="bait-thumb-blur">
                    <div className="bait-mini-header" />
                    <div className="bait-mini-line w80" />
                    <div className="bait-mini-line w60" />
                    <div className="bait-mini-section" />
                    <div className="bait-mini-line w90" />
                    <div className="bait-mini-line w50" />
                  </div>
                  <div className="bait-lock-overlay">
                    <Eye size={16} />
                  </div>
                </div>
                <span className="bait-name">{tmpl.label}</span>
                <Badge variant="secondary" className="bait-pro-tag">Pro</Badge>
                <span className="bait-benefit">{isRTL ? 'اضغط للمعاينة' : 'Tap to preview'}</span>
              </button>
            ))}
            {/* Free template — Simple */}
            <button
              className="premium-bait-card free-template-card"
              onClick={handlePreview}
              data-testid="free-template-simple"
            >
              <div className="bait-thumb free-thumb">
                <div className="bait-mini-header" />
                <div className="bait-mini-line w80" />
                <div className="bait-mini-line w60" />
                <div className="bait-mini-section" />
                <div className="bait-mini-line w90" />
                <div className="bait-mini-line w50" />
              </div>
              <span className="bait-name">{isRTL ? 'بسيط' : 'Simple'}</span>
              <Badge variant="outline" className="bait-free-tag" data-testid="free-badge">{isRTL ? 'مجاني' : 'Free'}</Badge>
              <span className="bait-benefit">{isRTL ? 'اضغط للمعاينة' : 'Tap to preview'}</span>
            </button>
          </div>

          {/* iter294 — Coin earning rules are owned by the Wallet
              page (single source of truth). We no longer hardcode a
              separate list here — that's how CV Builder and Wallet
              fell out of sync on production. Instead, surface a short
              call-to-action that opens /wallet, where the canonical
              tasks (Complete profile +20 · Post first ad +10 · Daily
              login +2 · Refer friend +25 …) are rendered from the
              backend `/api/wallet.tasks` payload. */}
          <div className="coin-earn-section" data-testid="coin-earn-section">
            <h4 className="coin-earn-title">{isRTL ? 'كيف تكسب العملات؟' : 'How to earn coins?'}</h4>
            <p className="coin-earn-subtitle" style={{ margin: '6px 0 10px', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
              {isRTL
                ? 'اربح العملات من مهام محفظتك واستخدمها لفتح قوالب السيرة الذاتية وميزات Pro.'
                : 'Earn coins from your wallet tasks and use them for CV templates and Pro features.'}
            </p>
            <button
              type="button"
              className="coin-earn-cta"
              onClick={() => navigate('/wallet')}
              data-testid="coin-earn-open-wallet"
              style={{
                background: '#FFF7ED',
                color: '#C2410C',
                border: '1px solid #FED7AA',
                borderRadius: 10,
                padding: '8px 14px',
                fontWeight: 700,
                fontSize: 13,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
              }}
            >
              {isRTL ? 'افتح محفظتي' : 'Open my wallet'}
              <ChevronRight size={14} style={isRTL ? { transform: 'scaleX(-1)' } : undefined} />
            </button>
          </div>
        </div>
      )}

      {/* "Almost Ready" encouragement — appears at ≥80% strength */}
      {!COINS_DISABLED && cvStrength.pct >= 80 && !hasProAccess && (
        <div className="almost-ready-card" data-testid="almost-ready-card">
          <Sparkles size={16} />
          <div className="almost-ready-text">
            <strong>{isRTL ? 'سيرتك الذاتية شبه جاهزة!' : 'Your CV is almost ready!'}</strong>
            <span>{isRTL ? 'افتح قوالب Pro لتصدير سيرة ذاتية احترافية.' : 'Unlock Pro templates to export a professional CV.'}</span>
          </div>
          <Button size="sm" onClick={() => setShowUnlockModal(true)} className="almost-ready-btn" data-testid="almost-ready-unlock">
            <Lock size={14} />
            {isRTL ? `فتح Pro (${exportCost} عملات)` : `Unlock Pro (${exportCost} coins)`}
          </Button>
        </div>
      )}

      {/* Sticky Bottom CTA Area */}
      <div className="cv-sticky-cta" data-testid="cv-sticky-cta">
        {/* Compact save indicator */}
        <button
          onClick={handleSave}
          disabled={saving || !canSave}
          className={`cv-save-ghost ${isDirty && canSave ? 'dirty' : ''}`}
          data-testid="cv-save"
        >
          {saving ? <Loader2 size={14} className="spin" /> : isDirty ? <Save size={14} /> : <Check size={14} />}
          <span>{saving ? (isRTL ? 'جارٍ الحفظ...' : 'Saving...') : isDirty ? (isRTL ? 'حفظ السيرة' : 'Save') : (isRTL ? 'تم الحفظ تلقائياً' : 'Saved automatically')}</span>
        </button>

        {/* Primary: Preview */}
        <Button
          onClick={handlePreview}
          disabled={previewing || !canSave}
          className="cta-preview"
          data-testid="cv-preview"
        >
          {previewing ? <Loader2 size={18} className="spin" /> : <Eye size={18} />}
          {isRTL ? 'معاينة' : 'Preview'}
        </Button>

        {/* Secondary: Download CV */}
        <Button
          variant="outline"
          onClick={handleDownloadCV}
          disabled={!canSave || exporting}
          className="cta-download"
          data-testid="cv-download"
        >
          {exporting ? <Loader2 size={18} className="spin" /> : <Download size={18} />}
          {(COINS_DISABLED || hasProAccess)
            ? (isRTL ? 'تنزيل السيرة الذاتية' : 'Download CV')
            : (isRTL ? 'تحميل السيرة الذاتية الأساسية' : 'Download Basic CV')
          }
        </Button>

        {/* Upsell: Unlock Pro — only for non-pro web users (hidden on Play build) */}
        {COINS_DISABLED ? null : !hasProAccess ? (
          <>
            <div className="cta-value-box" data-testid="cta-value-box">
              <h4>{isRTL ? 'لماذا الترقية إلى Pro؟' : 'Why upgrade to Pro?'}</h4>
              <ul>
                <li><Check size={12} /><span>{isRTL ? 'قوالب أكثر احترافية' : 'Professional templates'}</span></li>
                <li><Check size={12} /><span>{isRTL ? 'تنزيل بدون علامة مائية' : 'Download without watermark'}</span></li>
                <li><Check size={12} /><span>{isRTL ? 'مظهر أفضل أمام صاحب العمل' : 'Better recruiter impression'}</span></li>
                <li><Check size={12} /><span>{isRTL ? 'أدوات خطاب التقديم مضمّنة' : 'Cover letter tools included'}</span></li>
              </ul>
            </div>
            <Button
              onClick={() => setShowUnlockModal(true)}
              className="cta-unlock"
              data-testid="cv-unlock-pro"
              style={{ whiteSpace: 'normal', lineHeight: 1.2, minHeight: 56 }}
            >
              {/* iter294 — Stacked label so the button never clips on
                  small mobile screens. The primary action sits on top
                  ("Unlock Pro · 10 coins" / "فتح Pro · 10 عملات"); the
                  live balance shows underneath in smaller text. */}
              <Lock size={18} />
              <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, marginInlineStart: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                  {isRTL ? `فتح Pro · ${exportCost} عملات` : `Unlock Pro · ${exportCost} coins`}
                </span>
                <span style={{ fontSize: 11, opacity: 0.85, fontWeight: 500 }} data-testid="cta-unlock-balance">
                  {isRTL ? `رصيدك ${liveBalance ?? user?.coins ?? 0}` : `Balance ${liveBalance ?? user?.coins ?? 0}`}
                </span>
              </span>
            </Button>
            <p className="cta-reassurance" data-testid="cta-reassurance">
              {isRTL ? `فتح لمرة واحدة مقابل ${exportCost} عملات. لا يوجد اشتراك.` : `One-time unlock with ${exportCost} coins. No subscription.`}
            </p>
          </>
        ) : (
          <div className="cta-pro-unlocked" data-testid="cta-pro-unlocked">
            <Check size={16} />
            <span>{isRTL ? 'تم فتح Pro' : 'Pro Unlocked'}</span>
          </div>
        )}
      </div>

      {/* Canva-style Pro Template Preview Modal */}
      {showProPreview && (
        <div className="pro-preview-overlay" onClick={() => setShowProPreview(null)} data-testid="pro-preview-overlay">
          <div className="pro-preview-modal" onClick={e => e.stopPropagation()} data-testid="pro-preview-modal">
            <div className="pro-preview-header">
              <h3>{showProPreview.label} {isRTL ? '(معاينة)' : '(Preview)'}</h3>
              <button className="pro-preview-close" onClick={() => setShowProPreview(null)} data-testid="pro-preview-close">
                <X size={18} />
              </button>
            </div>
            <div className="pro-preview-body">
              {/* Simulated template preview with user data */}
              <div className="pro-preview-doc">
                <div className="pro-doc-header-bar" />
                <div className="pro-doc-name">{cv.personal?.full_name || (isRTL ? 'اسمك هنا' : 'Your Name')}</div>
                <div className="pro-doc-contact">
                  {cv.personal?.email && <span>{cv.personal.email}</span>}
                  {(cv.personal?.phones?.[0] || cv.personal?.phone) && <span>{cv.personal.phones?.[0] || cv.personal.phone}</span>}
                </div>
                {cv.summary && <div className="pro-doc-section"><div className="pro-doc-section-title">{isRTL ? 'الملخص' : 'Summary'}</div><p>{cv.summary.substring(0, 120)}...</p></div>}
                {(cv.skills || []).length > 0 && (
                  <div className="pro-doc-section">
                    <div className="pro-doc-section-title">{isRTL ? 'المهارات' : 'Skills'}</div>
                    <div className="pro-doc-skills">{cv.skills.slice(0, 5).map((s, i) => <span key={i} className="pro-doc-skill">{s}</span>)}</div>
                  </div>
                )}
                <div className="pro-doc-section"><div className="pro-doc-line w90" /><div className="pro-doc-line w70" /><div className="pro-doc-line w80" /></div>
                {/* Watermark overlay */}
                <div className="pro-preview-watermark" data-testid="pro-preview-watermark">WASSLA PREVIEW</div>
              </div>
            </div>
            <div className="pro-preview-footer">
              <Button variant="outline" disabled className="pro-preview-dl-disabled" data-testid="pro-preview-dl-disabled">
                <Download size={16} />
                {isRTL ? 'التنزيل مقفل' : 'Download Locked'}
              </Button>
              <Button onClick={() => { setShowProPreview(null); setShowUnlockModal(true); }} className="pro-preview-unlock" data-testid="pro-preview-unlock">
                <Lock size={16} />
                {isRTL ? 'افتح Pro للمعاينة الكاملة' : 'Unlock Pro for full preview'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SECTION CARD COMPONENT
// ============================================

function SectionCard({ title, icon, expanded, onToggle, children, count, testId, isComplete }) {
  return (
    <Card className="section-card" data-testid={testId}>
      <CardHeader className="section-header" onClick={onToggle} data-expanded={expanded}>
        <div className="section-title-row">
          {icon}
          <CardTitle>{title}</CardTitle>
          {count !== undefined && count > 0 && (
            <Badge variant="secondary">{count}</Badge>
          )}
          {isComplete !== undefined && (
            <span className={`section-status ${isComplete ? 'complete' : 'incomplete'}`}>
              {isComplete ? <Check size={10} /> : null}
              {isComplete ? '' : ''}
            </span>
          )}
        </div>
        <ChevronDown size={20} />
      </CardHeader>
      {expanded && (
        <CardContent className="section-content">
          {children}
        </CardContent>
      )}
    </Card>
  );
}

// ============================================
// CV PREVIEW MODAL COMPONENT
// ============================================

// ============================================
// CV PREVIEW MODAL - Print-friendly professional layout
// Section order: Personal, Summary, Work Experience, Certifications, Skills, Education, Languages, References
// ============================================

// Helper: format date range
function formatDateRange(start, end) {
  const s = start ? String(start).trim() : '';
  const e = end ? String(end).trim() : '';
  if (!s && !e) return '';
  if (s && !e) return `${s} – Present`;
  if (!s && e) return e;
  return `${s} – ${e}`;
}

// Helper: Info pill component
function InfoPill({ label }) {
  if (!label) return null;
  return (
    <span className="cv-info-pill">
      {label}
    </span>
  );
}

// Helper: Section component with divider line
function PreviewSection({ title, children }) {
  return (
    <section className="cv-section">
      <div className="cv-section-header">
        <h2 className="cv-section-title">{title}</h2>
        <div className="cv-section-divider" />
      </div>
      <div className="cv-section-content">{children}</div>
    </section>
  );
}

function CvPreviewModal({ cv, cvType = 'professional', onClose, t, lang, isRTL, noWatermark = false, onLocalExport, localExporting = false }) {
  // Normalize and clean data
  const personal = cv.personal || {};
  const employment = cleanList(cv.employment);
  const experience = cleanList(cv.experience);
  const education = cleanList(cv.education);
  const certifications = cleanList(cv.certifications);
  const references = cleanList(cv.references);
  const skills = (cv.skills || []).filter(s => String(s).trim());
  const languages = (cv.languages || []).filter(l => {
    if (typeof l === 'string') return l.trim();
    return (l?.name || '').trim();
  });

  const handlePrint = () => {
    window.print();
  };

  // Render Professional Resume Preview
  const renderProfessionalPreview = () => (
    <>
      {/* SUMMARY */}
      {cv.summary?.trim() && (
        <PreviewSection title={t('cvBuilder.sections.summary')}>
          <p className="cv-summary-text">{cv.summary}</p>
        </PreviewSection>
      )}

      {/* WORK EXPERIENCE */}
      {employment.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.employment')}>
          <div className="cv-entries">
            {employment.map((job, idx) => (
              <div key={idx} className="cv-entry break-inside-avoid">
                <div className="cv-entry-header">
                  <div className="cv-entry-main">
                    <div className="cv-entry-title">{job.title || t('cvBuilder.fields.jobTitle')}</div>
                    <div className="cv-entry-org">{job.company || ''}</div>
                  </div>
                  <div className="cv-entry-date">
                    {formatDateRange(job.start, job.end)}
                    {job.state_key && ` • ${job.state_key}`}
                  </div>
                </div>
                {Array.isArray(job.bullets) && job.bullets.filter(b => String(b).trim()).length > 0 && (
                  <ul className="cv-bullets">
                    {job.bullets.filter(b => String(b).trim()).map((bullet, i) => (
                      <li key={i}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </PreviewSection>
      )}

      {/* CERTIFICATIONS */}
      {certifications.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.certifications')}>
          <ul className="cv-cert-list">
            {certifications.map((cert, idx) => (
              <li key={idx} className="cv-cert-item">
                <div className="cv-cert-main">
                  <span className="cv-cert-name">{cert.name || t('cvBuilder.fields.certName')}</span>
                  {cert.issuer && <span className="cv-cert-issuer"> • {cert.issuer}</span>}
                </div>
                <div className="cv-cert-date">
                  {formatDateRange(cert.startDate, cert.endDate)}
                </div>
              </li>
            ))}
          </ul>
        </PreviewSection>
      )}

      {/* SKILLS & COMPETENCES */}
      {skills.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.skills')}>
          <div className="cv-skills-grid">
            {skills.map((skill, idx) => (
              <span key={idx} className="cv-skill-tag">{skill}</span>
            ))}
          </div>
        </PreviewSection>
      )}

      {/* EDUCATIONAL BACKGROUND */}
      {education.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.education')}>
          <div className="cv-entries">
            {education.map((edu, idx) => (
              <div key={idx} className="cv-entry break-inside-avoid">
                <div className="cv-entry-header">
                  <div className="cv-entry-main">
                    <div className="cv-entry-title">{localizeSystemValue(edu.degree, lang, DEGREE_LABEL_MAP) || t('cvBuilder.fields.degree')}</div>
                    <div className="cv-entry-org">
                      {edu.institution || t('cvBuilder.fields.institution')}
                      {edu.major && ` • ${edu.major}`}
                    </div>
                  </div>
                  <div className="cv-entry-date">
                    {formatDateRange(edu.start, edu.end)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PreviewSection>
      )}

      {/* LANGUAGES */}
      {languages.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.languages')}>
          <ul className="cv-lang-list">
            {languages.map((lang, idx) => {
              const name = typeof lang === 'string' ? lang : (lang.name || '');
              const level = typeof lang === 'object' ? lang.level : null;
              // iter294 — Translate the LEVEL value at render time so
              // an Arabic CV never shows `native`, `advanced`, …
              const levelLabel = level ? localizeSystemValue(level, lang, LANGUAGE_LEVEL_LABEL_MAP) : null;
              return (
                <li key={idx} className="cv-lang-item">
                  <span className="cv-lang-name">{name}</span>
                  {levelLabel && <span className="cv-lang-level"> — {levelLabel}</span>}
                </li>
              );
            })}
          </ul>
        </PreviewSection>
      )}

      {/* REFERENCES */}
      {references.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.references')}>
          <div className="cv-entries">
            {references.map((ref, idx) => (
              <div key={idx} className="cv-entry cv-ref-entry break-inside-avoid">
                <div className="cv-ref-name">{ref.fullName || t('cvBuilder.fields.refFullName')}</div>
                <div className="cv-ref-role">
                  {[ref.post, ref.institution].filter(Boolean).join(' • ')}
                </div>
                <div className="cv-ref-contact">
                  {ref.address && <div>{ref.address}</div>}
                  <div className="cv-ref-contact-row">
                    {ref.email && <span>{ref.email}</span>}
                    {ref.phone && <span>{ref.phone}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PreviewSection>
      )}
    </>
  );

  // Render Normal Resume Preview (simpler)
  const renderNormalPreview = () => (
    <>
      {/* OBJECTIVE */}
      {cv.objective?.trim() && (
        <PreviewSection title={t('cvBuilder.sections.objective')}>
          <p className="cv-summary-text">{cv.objective}</p>
        </PreviewSection>
      )}

      {/* WORK EXPERIENCE (simpler) */}
      {experience.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.experience')}>
          <div className="cv-entries">
            {experience.map((exp, idx) => (
              <div key={idx} className="cv-entry break-inside-avoid">
                <div className="cv-entry-header">
                  <div className="cv-entry-main">
                    <div className="cv-entry-title">{exp.title || t('cvBuilder.fields.jobTitle')}</div>
                    <div className="cv-entry-org">{exp.company || ''}</div>
                  </div>
                  <div className="cv-entry-date">
                    {formatDateRange(exp.start, exp.end)}
                  </div>
                </div>
                {exp.notes?.trim() && (
                  <p className="cv-exp-notes">{exp.notes}</p>
                )}
              </div>
            ))}
          </div>
        </PreviewSection>
      )}

      {/* EDUCATIONAL BACKGROUND */}
      {education.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.education')}>
          <div className="cv-entries">
            {education.map((edu, idx) => (
              <div key={idx} className="cv-entry break-inside-avoid">
                <div className="cv-entry-header">
                  <div className="cv-entry-main">
                    <div className="cv-entry-title">{localizeSystemValue(edu.degree, lang, DEGREE_LABEL_MAP) || t('cvBuilder.fields.degree')}</div>
                    <div className="cv-entry-org">
                      {edu.institution || t('cvBuilder.fields.institution')}
                      {edu.major && ` • ${edu.major}`}
                    </div>
                  </div>
                  <div className="cv-entry-date">
                    {formatDateRange(edu.start, edu.end)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PreviewSection>
      )}

      {/* SKILLS */}
      {skills.length > 0 && (
        <PreviewSection title={t('cvBuilder.sections.skills')}>
          <div className="cv-skills-grid">
            {skills.map((skill, idx) => (
              <span key={idx} className="cv-skill-tag">{skill}</span>
            ))}
          </div>
        </PreviewSection>
      )}
    </>
  );

  return (
    <div className="cv-preview-overlay" onClick={onClose} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="cv-preview-modal cv-preview-print" onClick={e => e.stopPropagation()}>
        {/* Top Actions Bar - Hidden during print */}
        <div className="cv-preview-actions print-hide">
          <Button variant="outline" onClick={onClose} className="cv-action-btn">
            {isRTL ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
            <span>{t('back') || 'Back'}</span>
          </Button>
          <Button
            onClick={onLocalExport || handlePrint}
            disabled={localExporting}
            className="cv-action-btn cv-print-btn"
            data-testid="cv-preview-save-pdf"
          >
            <Download size={16} />
            <span>{onLocalExport
              ? (localExporting ? (isRTL ? 'جارٍ الحفظ…' : 'Saving…') : (isRTL ? 'حفظ PDF' : 'Save PDF'))
              : (t('cvBuilder.preview.printPdf') || 'Print / Save PDF')}</span>
          </Button>
        </div>

        {/* CV Paper Container - ONLY THIS PRINTS */}
        <div id="cv-print-root" className="cv-paper">
          {/* Watermark (free web users see this; absent on the Android build) */}
          {!noWatermark && (
            <div className="cv-watermark print-watermark" aria-hidden="true">
              <span>{t('cvBuilder.unlock.watermarkText')}</span>
              <span>{t('cvBuilder.unlock.watermarkText')}</span>
              <span>{t('cvBuilder.unlock.watermarkText')}</span>
            </div>
          )}
          <div className="cv-paper-inner">
            {/* PERSONAL INFO HEADER */}
            <header className="cv-personal-header">
              <div className="cv-personal-content">
                <h1 className="cv-name">{personal.full_name || t('cvBuilder.preview.unnamed')}</h1>
                {personal.title && (
                  <p className="cv-job-title">{personal.title}</p>
                )}
                <div className="cv-contact-row">
                  <InfoPill label={personal.email} />
                  {(personal.phones || [personal.phone]).filter(p => p?.trim()).map((p, i) => (
                    <InfoPill key={`phone-${i}`} label={p} />
                  ))}
                  <InfoPill label={personal.city || personal.state_key} />
                  {cvType === 'professional' && (personal.links || []).filter(l => l.url?.trim()).map((l, i) => (
                    <InfoPill key={`link-${i}`} label={`${l.label}: ${l.url}`} />
                  ))}
                  {cvType !== 'professional' && <InfoPill label={personal.linkedin} />}
                </div>
              </div>
            </header>

            {/* Render type-specific content */}
            {cvType === 'professional' ? renderProfessionalPreview() : renderNormalPreview()}
          </div>
        </div>

        {/* Footer Actions (mobile) */}
        <div className="cv-preview-footer print-hide">
          <Button variant="outline" onClick={onClose}>
            {t('close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CVBuilder;
